/**
 * `/trpc` responses that the tRPC server never produced.
 *
 * Cloudflare sits in front of the API. When its bot protection answers a request with a
 * managed challenge, or the edge or origin platform fails with a gateway error page, the
 * browser receives an HTML document instead of a tRPC JSON body. The tRPC links assume
 * JSON, so the failure used to surface as an opaque
 * `SyntaxError: Unexpected token '<', "<!DOCTYPE "... is not valid JSON` with the HTTP
 * status dropped by the batch stream link (Sentry CURIUM-FE-RE: a 403 challenge on the
 * public complaint key lookup, which never reached the API).
 *
 * The transport guard stops such a response before tRPC parses it and rejects with a
 * typed {@link EdgeResponseError}. tRPC wraps it as the `cause` of a `TRPCClientError`,
 * where the classifiers below find it.
 */

/**
 * Cloudflare sets this header, with the value `challenge`, on a challenge page served in
 * place of the origin response, so that scripts can recognise it.
 */
const CLOUDFLARE_MITIGATED_HEADER = 'cf-mitigated';

export class EdgeResponseError extends Error {
    /** HTTP status of the edge response, such as 403 for a challenge or 504 for a gateway timeout. */
    readonly status: number;
    /**
     * True when Cloudflare answered with a browser challenge. Only a page load can solve it,
     * so repeating the same request from script is stopped again.
     */
    readonly challenged: boolean;

    constructor(status: number, challenged: boolean) {
        super(
            challenged
                ? `API request stopped by a browser security check (HTTP ${status})`
                : `API request returned a web page instead of data (HTTP ${status})`,
        );
        this.name = 'EdgeResponseError';
        this.status = status;
        this.challenged = challenged;
    }
}

function isHtmlDocument(response: Response): boolean {
    return response.headers.get('content-type')?.toLowerCase().startsWith('text/html') === true;
}

/**
 * Wraps the tRPC request `fetch` so an HTML page (a Cloudflare challenge or an edge error
 * page) rejects with an {@link EdgeResponseError} carrying its status, instead of reaching
 * tRPC's JSON parser. JSON and event-stream responses pass through untouched.
 *
 * Without an explicit implementation the global `fetch` is resolved on every call, so the
 * instrumented global (Sentry breadcrumbs and trace propagation) is still the one used.
 */
export function withEdgeResponseGuard(fetchImpl?: typeof globalThis.fetch): typeof globalThis.fetch {
    return async (input, init) => {
        const response = fetchImpl ? await fetchImpl(input, init) : await globalThis.fetch(input, init);
        const challenged = response.headers.get(CLOUDFLARE_MITIGATED_HEADER) === 'challenge';
        if (!challenged && !isHtmlDocument(response)) {
            return response;
        }

        // The body is a page for a person, not data: release the connection without reading it.
        void response.body?.cancel().catch(() => undefined);
        throw new EdgeResponseError(response.status, challenged);
    };
}

/** The edge response behind a failed request, whether passed directly or as a `TRPCClientError` cause. */
export function getEdgeResponseError(error: unknown): EdgeResponseError | undefined {
    if (error instanceof EdgeResponseError) {
        return error;
    }
    if (error instanceof Error && error.cause instanceof EdgeResponseError) {
        return error.cause;
    }
    return undefined;
}

/** True when Cloudflare stopped the request with a browser challenge that only a page load can pass. */
export function isEdgeChallengeError(error: unknown): boolean {
    return getEdgeResponseError(error)?.challenged === true;
}
